import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core'
import { LoaderActionTypeEnum, LoaderFacade } from 'libs/shared/utils/loader/src'
import { GbAuctionBannerVisibilityService } from 'libs/gb-front/auction/util/src/lib/gb-auction-banner-visibility/gb-auction-banner-visibility.service'
import { CategoriesEntity, CategoriesFacade } from 'libs/gb-front/category/data-access/src'
import { ItemFacade } from 'libs/gb-front/item/data-access/src'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { delay, Observable, startWith, take, timer } from 'rxjs'
import { GbItemsObserverService } from 'libs/shared/ui/src/lib/gb-items-observer/gb-items-observer.service'
import { RouterUtils } from 'libs/shared/utils/helpers/src/lib/router-utils'
import { Router } from '@angular/router'
import * as CategoriesActions from 'libs/gb-front/category/data-access/src/lib/+state/categories.actions'
import * as ItemsActions from 'libs/gb-front/item/data-access/src/lib/+state/items.actions'

@UntilDestroy()
@Component({
  selector: 'gb-categories-list',
  templateUrl: './gb-categories-list.component.html',
  styleUrls: ['./gb-categories-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GbCategoriesListComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChildren('item', { read: ElementRef })
  categoryList?: QueryList<ElementRef>

  categories$ = this.categoriesFacade.allCategories$
  count$ = this.categoriesFacade.count$
  stalePages$ = this.categoriesFacade.stalePages$
  isLoading$ = this.loaderFacade.getIsLoading$(LoaderActionTypeEnum.LoadCategories)

  // category user navigated from
  fromUuid?: string | null

  constructor(
    private categoriesFacade: CategoriesFacade,
    private itemFacade: ItemFacade,
    private loaderFacade: LoaderFacade,
    private gbBannerVisibilityService: GbAuctionBannerVisibilityService,
    private itemsObserverService: GbItemsObserverService,
    private router: Router,
  ) {
    this.fromUuid =
      // returning to list after viewing category or category item
      RouterUtils.getPreviousNavigationParam(this.router, 'categoryId')
  }

  ngOnInit(): void {
    //TODO: this should be a simple dispatch and all the logic handles in the effect
    // this would make much easier to test and maintain
    this.setAllCategoriesStale()
    this.clearFilteredItems()
    this.hideBanner()

    this.categories$.pipe(take(1)).subscribe((categories) => {
      const action = categories.length
        ? CategoriesActions.resyncCategories(0) // resync first page if categories in state
        : CategoriesActions.loadCategories() // load categories if no categories in state
      this.categoriesFacade.dispatch(action)
    })
  }

  ngAfterViewInit(): void {
    this.categoryList?.changes
      .pipe(startWith(undefined), delay(500), untilDestroyed(this))
      .subscribe(() => this.itemsObserverService.setItemsToObserve(this.categoryList?.toArray()))

    if (this.fromUuid) {
      // scroll to selected category on returning to list
      document.getElementById(this.fromUuid)?.scrollIntoView()
    }
  }

  ngOnDestroy(): void {
    this.gbBannerVisibilityService.showBanner()
  }

  hideBanner(): Promise<void> {
    return Promise.resolve().then(() => {
      this.gbBannerVisibilityService.hideBanner()
    })
  }

  /**
   * Load next 5 categories when end of page reached and more categories to load
   * @param count
   * @param categories
   */
  onEndReached(count: number | null, categories: CategoriesEntity[] | null): void {
    if (this.isMoreCategoriesToLoad(count, categories)) {
      this.categoriesFacade.dispatch(CategoriesActions.loadCategories())
    }
  }

  /**
   * Checks if all categories from auction have been loaded in store
   * @param count
   * @param items
   */
  isMoreCategoriesToLoad(count: number | null, categories: CategoriesEntity[] | null): boolean {
    if (count === null || categories === null) return false
    return count > categories.length
  }

  /**
   * window focus event should resync all the necessary data from the API
   * as pusher event can be missed when the window has no focus, so when the user refocus
   * set all loaded categories as stale and call function to resync categories currently in view
   *
   * delaying 1 second for the categories resync on refocus to make sure that the urlName is updated before
   * in the cases of a change in urlName
   *
   * @param offset
   */

  onFocus(offset: number): void {
    this.setAllCategoriesStale()
    this.timer().subscribe(() => this.categoriesFacade.dispatch(CategoriesActions.resyncCategories(offset)))
  }

  timer(): Observable<number> {
    return timer(1000)
  }

  /**
   * Resync stale categories on scroll
   * @param offset
   */
  onScroll(offset: number): void {
    this.categoriesFacade.dispatch(CategoriesActions.resyncCategoriesOnScroll(offset))
  }

  /**
   * Track items added or removed to avoid unnecessary
   * re-rendering of categories list. This resulted in
   * the category preview elements being recreated and
   * scrolling to top after resyncing categories.
   * @param index
   * @param category
   * @link https://netbasal.com/angular-2-improve-performance-with-trackby-cc147b5104e5
   * @link https://tharakamd-12.medium.com/avoid-unnecessary-rendering-on-angular-ngfor-with-trackby-f56ae5d79561
   */
  trackCategory(index: number, category: CategoriesEntity): string {
    return category.uuid
  }

  /**
   * Clear filtered items in state
   */
  clearFilteredItems(): void {
    this.itemFacade.dispatch(ItemsActions.clearFilteredItems())
  }

  setAllCategoriesStale(): void {
    this.categoriesFacade.dispatch(CategoriesActions.setAllCategoriesStale())
  }
}
